import { el } from 'redom';

import * as basicEditorValueSerializer from 'basic-editor-value-serializer';

import { stringToSVG } from 'utilities';

import wandIconString from 'assets/images/ai-wand-icon.svg?raw';
import acceptIconString from 'assets/images/accept-icon.svg?raw';
import discardIconString from 'assets/images/small-close-icon.svg?raw';

import './result-overlay.scss';

const STATE_PENDING = 'pending';
const STATE_REVIEW = 'review';

class AIInstructionResultOverlay {

  constructor(configuration) {

    this.onAccept = configuration.onAccept;
    this.onDiscard = configuration.onDiscard;

    this.state = STATE_PENDING;

    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleAcceptClick = this.handleAcceptClick.bind(this);
    this.handleDiscardClick = this.handleDiscardClick.bind(this);

    this.buildHeader();
    this.buildPendingSection();
    this.buildReviewSection();

    this.el = el('.ai-instruction-result-overlay-main', [
      this.header,
      this.pendingSection,
      this.reviewSection
    ]);

    this.el.addEventListener('mousedown', this.handleMouseDown);

    this.applyState();

  }

  buildHeader() {

    this.headerIcon = stringToSVG(wandIconString);
    this.headerIcon.classList.add('ai-instruction-result-header-icon');

    this.headerTitle = el('.ai-instruction-result-header-title', 'AI suggestion');

    this.header = el('.ai-instruction-result-header', [
      this.headerIcon,
      this.headerTitle
    ]);

  }

  buildPendingSection() {

    this.skeletonLineOne = el('.ai-instruction-result-skeleton-line');
    this.skeletonLineTwo = el('.ai-instruction-result-skeleton-line');
    this.skeletonLineThree = el('.ai-instruction-result-skeleton-line.short');

    this.pendingBody = el('.ai-instruction-result-pending-body', [
      this.skeletonLineOne,
      this.skeletonLineTwo,
      this.skeletonLineThree
    ]);

    this.pendingLabel = el('.ai-instruction-result-pending-label', 'Generating…');

    this.pendingSection = el('.ai-instruction-result-pending', [
      this.pendingBody,
      this.pendingLabel
    ]);

  }

  buildReviewSection() {

    this.reviewText = el('.ai-instruction-result-review-text.hide-scrollbar');
    this.reviewExplanation = el('.ai-instruction-result-review-explanation');

    this.reviewBody = el('.ai-instruction-result-review-body', [
      this.reviewText,
      this.reviewExplanation
    ]);

    this.discardIcon = stringToSVG(discardIconString);
    this.discardButton = el('button.ai-instruction-result-discard-button', {
      title: 'Discard'
    }, [
      this.discardIcon,
      el('span.ai-instruction-result-button-label', 'Discard')
    ]);

    this.acceptIcon = stringToSVG(acceptIconString);
    this.acceptButton = el('button.ai-instruction-result-accept-button', {
      title: 'Accept'
    }, [
      this.acceptIcon,
      el('span.ai-instruction-result-button-label', 'Accept')
    ]);

    this.acceptButton.addEventListener('click', this.handleAcceptClick);
    this.discardButton.addEventListener('click', this.handleDiscardClick);

    this.reviewActions = el('.ai-instruction-result-review-actions', [
      this.discardButton,
      this.acceptButton
    ]);

    this.reviewSection = el('.ai-instruction-result-review', [
      this.reviewBody,
      this.reviewActions
    ]);

  }

  handleMouseDown(event) {

    event.preventDefault();

  }

  handleAcceptClick() {

    if (this.state !== STATE_REVIEW) {
      return;
    }

    this.onAccept();

  }

  handleDiscardClick() {

    this.onDiscard();

  }

  setPendingState() {

    this.state = STATE_PENDING;
    this.applyState();

  }

  setReviewState(response) {

    this.state = STATE_REVIEW;

    const replacementBlock = basicEditorValueSerializer.utilities.node.paragraph.buildNode({
      children: response.replacements
    });

    this.reviewText.innerHTML = basicEditorValueSerializer.toHTML([replacementBlock]);

    if (typeof response.explanation === 'string' && response.explanation.length > 0) {
      this.reviewExplanation.textContent = response.explanation;
      this.reviewExplanation.style.display = '';
    } else {
      this.reviewExplanation.textContent = '';
      this.reviewExplanation.style.display = 'none';
    }

    this.applyState();

  }

  positionAt(rect) {

    const style = this.el.style;
    style.position = 'fixed';
    style.top = rect.top + 'px';
    style.left = rect.left + 'px';
    style.minWidth = rect.width + 'px';

  }

  applyState() {

    if (this.state === STATE_PENDING) {
      this.pendingSection.style.display = '';
      this.reviewSection.style.display = 'none';
      return;
    }

    if (this.state === STATE_REVIEW) {
      this.pendingSection.style.display = 'none';
      this.reviewSection.style.display = '';
      return;
    }

  }

  onunmount() {

    this.el.removeEventListener('mousedown', this.handleMouseDown);
    this.acceptButton.removeEventListener('click', this.handleAcceptClick);
    this.discardButton.removeEventListener('click', this.handleDiscardClick);

  }

}

export default AIInstructionResultOverlay;